import { findNode, FormNode, formTraverse } from '@msdk/definitions'

import { ExternalValidation } from '../../models/form'
import { getValidationTarget } from './validation-panel-utils'

export interface ExternalValidationEntry {
  /** Id of the custom field node that reported the validation; the entry applies only while it is visible. */
  ownerId: string
  /** `null` means the owner reports the node as valid, which clears every other message. */
  validation: ExternalValidation | null
}

export type ExternalValidations = Map<string, ExternalValidationEntry>

export function isSameExternalValidation(a?: ExternalValidationEntry, b?: ExternalValidationEntry): boolean {
  if (!a || !b) {
    return a === b
  }

  return (
    a.ownerId === b.ownerId &&
    a.validation?.code === b.validation?.code &&
    (a.validation?.params ?? []).join('\u0000') === (b.validation?.params ?? []).join('\u0000')
  )
}

/** Replaces the messages of the nodes that have an external validation, so it overrides rule engine results. */
export function applyExternalValidations(root: FormNode, entries: ExternalValidations): void {
  if (!entries.size) {
    return
  }

  const ownerVisibility = new Map<string, boolean>()
  const isOwnerVisible = (ownerId: string) => {
    if (!ownerVisibility.has(ownerId)) {
      ownerVisibility.set(ownerId, findNode(root, ownerId)?.value.visibility === 'Visible')
    }
    return ownerVisibility.get(ownerId) as boolean
  }

  formTraverse(root, (node) => {
    const entry = entries.get(getValidationTarget(node))
    if (!entry || !isOwnerVisible(entry.ownerId)) {
      return
    }

    const { validation } = entry
    node.value.validationMessages = validation ? [validation.code] : []
    node.value.validationParams = validation?.params ?? []
    node.value.validationMessageParams = validation ? [{ code: validation.code, params: validation.params ?? [] }] : []
    node.value.validationState = validation ? 'Invalid' : 'Valid'
  })
}
